import { z } from "zod";

import { periodSchema } from "./common.schema";

// =============================================================================
// TWR Mode
// =============================================================================

export const twrModeSchema = z.enum(["simple", "advanced", "mixed"]);

export type TWRMode = z.infer<typeof twrModeSchema>;

export const bookTypeSchema = z.enum([
  "general",
  "cash",
  "securities",
  "crypto",
]);

export type BookType = z.infer<typeof bookTypeSchema>;

// =============================================================================
// Book Schema
// =============================================================================

export const bookSchema = z.object({
  book_id: z.string(),
  name: z.string(),
  provider: z.string(),
  book_type: bookTypeSchema.optional(),
  record_ids: z.array(z.string()),
  update_date: z.string(),
  expired: z.boolean(),
  twr_mode: twrModeSchema.optional(),
});

export type Book = z.infer<typeof bookSchema>;

// =============================================================================
// Chart Schema (legacy)
// =============================================================================

export const chartSchema = z.object({
  chart_id: z.string(),
  asset_id_buy: z.string(),
  asset_id_sell: z.string(),
  update_date: z.string(),
  interval: z.string(),
  prices: z.array(z.number()),
});

export type Chart = z.infer<typeof chartSchema>;

// =============================================================================
// Value Chart Schemas
// =============================================================================

export const valuePointSchema = z.object({
  timestamp: z.string(),
  price: z.number(),
});

export const valueChartSchema = z.object({
  value_chart_id: z.string(),
  book_id: z.string().optional(),
  user_id: z.string(),
  update_date: z.string(),
  period: periodSchema,
  interval: z.string(),
  value_points: z.array(valuePointSchema),
  last_timestamp: z.string().nullable().optional(),
});

export type ValuePoint = z.infer<typeof valuePointSchema>;
export type ValueChart = z.infer<typeof valueChartSchema>;

// =============================================================================
// TWR Chart Schemas
// =============================================================================

export const twrPointSchema = z.object({
  timestamp: z.string(),
  twr_cumulative: z.number(),
  bmv: z.number().optional(),
  cf: z.number().optional(),
});

export const twrChartSchema = z.object({
  twr_chart_id: z.string(),
  user_id: z.string(),
  book_id: z.string().nullable().optional(),
  update_date: z.string(),
  period: periodSchema,
  interval: z.string(),
  twr_points: z.array(twrPointSchema),
  last_timestamp: z.string().nullable().optional(),
  twr_mode: twrModeSchema,
  annualized_return_since_inception: z.number().nullable().optional(),
});

export type TWRPoint = z.infer<typeof twrPointSchema>;
export type TWRChart = z.infer<typeof twrChartSchema>;

// =============================================================================
// Performance Schemas
// =============================================================================

export const assetPerformanceSchema = z.object({
  performance_absolute: z.number(),
  performance_percentage: z.number(),
  performance_absolute_native: z.number(),
  performance_percentage_native: z.number(),
  current_value: z.number(),
  current_value_native: z.number(),
  start_value: z.number(),
  start_value_native: z.number(),
  total_cost: z.number(),
  total_cost_native: z.number(),
  avg_cost_basis: z.number().nullable().optional(),
  avg_cost_basis_native: z.number().nullable().optional(),
  shares_owned: z.number().nullable().optional(),
  // P&L Breakdown
  realized_pnl: z.number(),
  realized_pnl_native: z.number(),
  unrealized_pnl: z.number(),
  unrealized_pnl_native: z.number(),
  dividends_received: z.number(),
  dividends_received_native: z.number(),
  // Position status
  is_closed: z.boolean(),
  has_history: z.boolean(),
  // Asset info
  asset_name: z.string(),
  asset_symbol: z.string(),
  asset_type: z.string(),
  asset_currency: z.string(),
  asset_website: z.string().nullable().optional(),
});

export const totalPerformanceSchema = z.object({
  total_performance_absolute: z.number(),
  total_performance_percentage: z.number(),
  total_current_value: z.number(),
  total_cost: z.number(),
  realized_pnl: z.number(),
  unrealized_pnl: z.number(),
});

export const performanceDataSchema = z.object({
  user_id: z.string(),
  time_period: z.string(),
  period_label: z.string(),
  assets_performance: z.record(z.string(), assetPerformanceSchema),
  total_performance: totalPerformanceSchema,
});

export const performanceResultSchema = z.object({
  id: z.string(),
  period: periodSchema,
  user_id: z.string(),
  book_id: z.string().nullable(),
  data: performanceDataSchema,
  update_date: z.string(),
});

export const assetValuationSchema = z.object({
  asset_id: z.string(),
  quantity: z.number(),
  price_native: z.number(),
  fx_rate: z.number(),
  value_base: z.number(),
  is_fiat: z.boolean(),
  currency_id: z.string().optional(),
});

export const currentValuationSnapshotSchema = z.object({
  snapshot_id: z.string(),
  user_id: z.string(),
  book_id: z.string().nullable().optional(),
  as_of: z.string(),
  update_date: z.string(),
  base_currency_id: z.string(),
  total_current_value: z.number(),
  assets: z.record(z.string(), assetValuationSchema),
});

export type AssetPerformance = z.infer<typeof assetPerformanceSchema>;
export type TotalPerformance = z.infer<typeof totalPerformanceSchema>;
export type PerformanceData = z.infer<typeof performanceDataSchema>;
export type PerformanceResult = z.infer<typeof performanceResultSchema>;
export type AssetValuation = z.infer<typeof assetValuationSchema>;
export type CurrentValuationSnapshot = z.infer<
  typeof currentValuationSnapshotSchema
>;

// =============================================================================
// Benchmark Schemas
// =============================================================================

export const benchmarkPointSchema = z.object({
  timestamp: z.string(),
  return_pct: z.number(),
});

export const benchmarkSelectionSchema = z.object({
  enabled: z.boolean(),
  assetId: z.string().nullable(),
  assetSymbol: z.string().nullable(),
  assetName: z.string().nullable(),
});

export type BenchmarkPoint = z.infer<typeof benchmarkPointSchema>;
export type BenchmarkSelection = z.infer<typeof benchmarkSelectionSchema>;
